// Wann ist der Risiken-Reiter ein IMMOBILIEN-Reiter?
//
// Ein Immobilienunternehmen trägt andere Risiken als ein Betrieb: Es hat kein
// Anlagevermögen im Sinne der Matrix, keinen Ertragsausfall aus Produktion und
// keine Objektmappen quer über Kfz und Maschinen. Es hat Gebäude — und die
// Betrachtung läuft je Gebäude, nicht je Vermögensposten. Deshalb wechselt der
// Reiter die Gliederung, sobald der ganze Verbund in der Immobilienwirtschaft
// steht.
//
// Der Umschalter ist bewusst STRENG: Sobald EINE Firma des Verbunds erkennbar
// etwas anderes tut (Stahlbau, Handel), wäre die reine Immobilienansicht
// falsch — deren Risiken fielen komplett aus dem Reiter. Umgekehrt darf eine
// Firma OHNE gepflegte Branche den Wechsel nicht verhindern: In fast jedem
// Verbund gibt es eine Gesellschaft, an der noch niemand die Branche gesetzt
// hat, und ein einziger leerer Datensatz würde die Ansicht für alle anderen
// unbrauchbar machen.

import { findLeaf, type BrancheLeaf } from "../../data/brancheClassification";
import type { CompanyNetworkFirma } from "../../services/companyNetworkService";

/** Ebene-1-Bezeichnung des Katalogs (`branchenklassifizierung.ts`). */
export const IMMOBILIENWIRTSCHAFT = "Immobilienwirtschaft";

/** Alle Ebene-1-Branchen einer Firma (Hauptbranche + weitere Zuordnungen). */
function ebene1Von(rows: BrancheLeaf[], firma: CompanyNetworkFirma): string[] {
  const ids = [
    firma.brancheClassId,
    ...(firma.weitereBranchen ?? []).map((w) =>
      typeof w === "number" ? w : w.brancheClassId,
    ),
  ].filter((id): id is number => id != null);
  const out: string[] = [];
  for (const id of ids) {
    const e1 = findLeaf(rows, id)?.ebene1?.trim();
    if (e1 && !out.includes(e1)) out.push(e1);
  }
  return out;
}

/** Trägt diese Firma (mindestens auch) die Immobilienwirtschaft? */
export function istImmobilienfirma(
  rows: BrancheLeaf[],
  firma: CompanyNetworkFirma,
): boolean {
  return ebene1Von(rows, firma).includes(IMMOBILIENWIRTSCHAFT);
}

/**
 * Ist der geöffnete Kontakt ein Immobilienunternehmen in einem Verbund, der
 * durchgehend Immobilienwirtschaft ist?
 *
 * Zwei Bedingungen, beide notwendig:
 *  1. Die geöffnete Firma selbst steht in der Immobilienwirtschaft. Ohne sie
 *     ist der Reiter nicht ihr Reiter — eine Bauträger-Tochter macht aus einem
 *     Stahlbauer kein Immobilienunternehmen.
 *  2. KEINE Firma des Verbunds trägt eine andere Branche — auch nicht
 *     zusätzlich. Eine Gesellschaft, die neben der Vermietung Handel betreibt,
 *     bringt Handelsrisiken mit, und die zeigt die Immobilienansicht nicht.
 *     Firmen ganz ohne Branche zählen als „unbekannt", nicht als „andere"
 *     (siehe Kopf).
 *
 * `rows` leer (Katalog noch nicht geladen) ⇒ false: lieber einen Wimpernschlag
 * die gewohnte Ansicht als ein Aufblitzen der falschen Gliederung.
 */
export function istImmobilienVerbund(
  rows: BrancheLeaf[],
  firmen: CompanyNetworkFirma[],
  selfId: number,
): boolean {
  if (!rows.length || !firmen.length) return false;
  const self = firmen.find((f) => f.id === selfId);
  if (!self || !istImmobilienfirma(rows, self)) return false;
  return firmen.every((f) => {
    const e1 = ebene1Von(rows, f);
    return e1.length === 0 || e1.every((x) => x === IMMOBILIENWIRTSCHAFT);
  });
}
